import { SocketV2State } from './stateMachine';
import { SocketStats } from './types';

/**
 * Owns the counters behind getStats() and the single outage clock. Downtime accrues from the
 * moment an outage starts until the next successful connect; a snapshot taken mid-outage adds
 * the in-flight span so the number never looks stale.
 */
export class SocketStatsTracker {
  private stats = { connectCount: 0, dropCount: 0, lastError: undefined as string | undefined, totalDowntimeMs: 0 };
  private outageStartedAt?: number;

  /** Handshake completed: count the connection and close out any running outage. */
  public onConnected = (): void => {
    this.stats.connectCount += 1;
    this.endOutage();
  };

  /** Transport dropped: count the drop and start the outage clock. */
  public onDropped = (): void => {
    this.stats.dropCount += 1;
    this.startOutage();
  };

  /** Outage clock only — used where downtime accrues without a drop (e.g. going offline). */
  public startOutage = (): void => {
    if (this.outageStartedAt === undefined) {
      this.outageStartedAt = Date.now();
    }
  };

  public endOutage = (): void => {
    if (this.outageStartedAt !== undefined) {
      this.stats.totalDowntimeMs += Date.now() - this.outageStartedAt;
      this.outageStartedAt = undefined;
    }
  };

  public setLastError = (tag: string): void => {
    this.stats.lastError = tag;
  };

  public snapshot = (currentState: SocketV2State, queueDepth: number): SocketStats => ({
    connectCount: this.stats.connectCount,
    currentState,
    dropCount: this.stats.dropCount,
    lastError: this.stats.lastError,
    queueDepth,
    totalDowntimeMs:
      this.stats.totalDowntimeMs + (this.outageStartedAt === undefined ? 0 : Date.now() - this.outageStartedAt),
  });
}
